import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  Tabs,
  Tab,
  Alert
} from '@mui/material';
import { 
  PendingActions, 
  People, 
  Cancel,
  Dashboard as DashboardIcon
} from '@mui/icons-material';
import { apiService } from '../../config/api';
import ResearcherApplications from './ResearcherApplications';
import ResearcherManagement from './ResearcherManagement';
import RejectedApplications from './RejectedApplications';
import MasterStats from './MasterStats';
import LatestUpdatesCard from '../common/LatestUpdatesCard';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`master-tabpanel-${index}`}
      aria-labelledby={`master-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const MasterDashboard: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [approvedCount, setApprovedCount] = useState(0);
  const [rejectedCount, setRejectedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load pending applications count, approved researchers count, and rejected applications count
      const [pendingResponse, rejectedResponse, researchersResponse] = await Promise.all([
        apiService.get('/api/admin/researcher-applications?status=pending&limit=1'),
        apiService.get('/api/admin/researcher-applications?status=rejected&limit=1'),
        apiService.get('/api/admin/researchers?status=approved')
      ]);
      
      if (pendingResponse.success) {
        setPendingCount(pendingResponse.totalCount || 0);
      }
      
      if (rejectedResponse.success) {
        setRejectedCount(rejectedResponse.totalCount || 0);
      }
      
      if (researchersResponse.success) {
        setApprovedCount(researchersResponse.totalCount || researchersResponse.researchers?.length || 0);
      }
      
    } catch (err: any) {
      setError('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const refreshPendingCount = () => {
    loadDashboardData();
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box
        sx={{
          mb: 4,
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", lg: "flex-start" },
          gap: 3,
          flexDirection: { xs: "column", lg: "row" },
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" gutterBottom color="primary">
            <DashboardIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
            Master Account Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage researcher applications and platform access
          </Typography>
        </Box>
        <LatestUpdatesCard editable title="Latest Updates" variant="compact" maxItems={5} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Quick Stats */}
      <MasterStats loading={loading} />

      {/* Main Content Tabs */}
      <Card elevation={3} sx={{ mt: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={currentTab} 
            onChange={handleTabChange}
            aria-label="master dashboard tabs"
            variant="fullWidth"
          >
            <Tab 
              icon={<PendingActions />}
              label={`Pending Applications (${pendingCount})`}
              id="master-tab-0"
              aria-controls="master-tabpanel-0"
            />
            <Tab 
              icon={<People />}
              label={`Approved Researchers (${approvedCount})`}
              id="master-tab-1"
              aria-controls="master-tabpanel-1"
            />
            <Tab 
              icon={<Cancel />}
              label={`Rejected Applications (${rejectedCount})`}
              id="master-tab-2"
              aria-controls="master-tabpanel-2"
            />
          </Tabs>
        </Box>

        <TabPanel value={currentTab} index={0}>
          <ResearcherApplications 
            onApplicationProcessed={refreshPendingCount}
          />
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <ResearcherManagement />
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <RejectedApplications 
            onApplicationProcessed={refreshPendingCount}
          />
        </TabPanel>
      </Card>
    </Container>
  );
};

export default MasterDashboard;
